/**
 * Still-live older matches the user marked/viewed — `GET /api/matches/saved`.
 *
 * Renders as a dense list (`MatchListRow`), not the `MatchCard` grid Dashboard
 * uses: this page is a backlog someone scans, not fresh matches being
 * evaluated one at a time (PIVOT-127, 2026-08-07-plan-frontend-redesign.md
 * §3c). No page-level action exists for this page today, so `.page-header`
 * carries the title only — do not add one just to fill the row.
 *
 * Also the candidate's own application-tracking log (PIVOT-112): the status
 * control and "did you hear back?" nudge render here because this is the one
 * page that keeps a saved match around after the day it was scored — the
 * dashboard shows only today's cards, so this is where a candidate comes back
 * to update what happened with a job they applied to. The candidate is
 * reporting a fact about their own life through these two, and a card that
 * silently drifted from what the server actually stored would be a lie
 * (constitution §I).
 *
 * PIVOT-133: all three writes now revert on failure and say why, near the row
 * they touched — `interestFailed`/`statusFailed`/`outcomeFailed` on
 * `MatchListRow`. The interest tap used to be a genuine exception (best-effort,
 * no rollback); it no longer is, because "the card silently disagrees with the
 * server until a reload" is the same lie for a taste signal as it is for a
 * fact, once nothing on screen says the write failed.
 *
 * PIVOT-141: partitioned into two headed sections rather than one flat list —
 * a dismissed job used to sit under the same "Saved" heading a saved one did,
 * distinguished only by which button held the green pill. `GET
 * /api/matches/saved` guarantees every row has a signal (interest or an
 * application status, `routes/matches.py`'s `get_saved_matches`), so the two
 * predicates below are exhaustive and non-overlapping: an application status
 * outranks a stale "not interested" tap, since the funnel status is the
 * candidate's authoritative record of where a job stands (frontend/CLAUDE.md
 * "carry a fact across a boundary" — application_status is that fact here,
 * not `interest`). Reuses Dashboard's `<h2>`-per-list pattern rather than
 * inventing tab machinery, and fixes the heading skip this page had —
 * `h1` straight to `MatchListRow`'s `h3`, nothing at `h2`.
 *
 * PIVOT-146: the empty case (no saved and no dismissed rows) is a dashed, quiet
 * box plus a link back to `/dashboard` — the previous version was a title and
 * one sentence on a dead-end page with nothing to click. The treatment was
 * briefly shared with `DryDayNotice` as `styles/empty-state.css`; PIVOT-142
 * then made the dry-day notice the *loudest* thing on the dashboard (on a dry
 * day that notice is the page), leaving this the only caller, so §3's
 * ownership rule puts the rules back in this file.
 *
 * PIVOT-373 A5: a third section, ahead of the other two — **No longer
 * current**. A saved row outlives the day it was scored, which is this page's
 * whole point, so a delisted posting or one whose stored decision has moved
 * off `recommended` would otherwise keep showing yesterday's verdict as
 * today's. `Match.recommendation_current` is the server's own answer to that
 * (`routes/matches.py::get_saved_matches`, decided on the same path the
 * dashboard's own slate comes from); this page renders it and never re-derives
 * it from `tag` or `job.live`. The partition is by heading rather than by a
 * badge on the row because `MatchListRow` is shared with nothing else that
 * knows this fact — and a heading a reader cannot miss beats a pill they can.
 *
 * Inside that section, `not_current_reason` splits the rows into one group per
 * reason, each with its own line: a posting that was taken down and a job your
 * own rules stopped recommending are two different things to tell someone, and
 * the API says which. A row the server gave no reason for keeps its place with
 * no line — this page states what it was told and nothing more.
 */

import { useState } from 'react'
import { Link } from 'react-router'
import { isAbortError } from '../api/client'
import { MatchListRow } from '../components/MatchListRow'
import {
  getSavedMatches,
  reportGone,
  reportOutcome,
  setApplicationStatus,
  setInterest,
} from '../api/matches'
import { useApiResource } from '../hooks/useApiResource'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n/types'
import type { ApplicationStatus, Interest, Match, NotCurrentReason } from '../types/domain'
import './SavedSeen.css'

/** PIVOT-136: opts this read into `useApiResource`'s stale-while-revalidate
 * cache, so navigating away and back paints the last list immediately while
 * a fresh one loads underneath it, same as `Dashboard`. */
const SAVED_MATCHES_CACHE_KEY = 'saved-seen-matches'

/** PIVOT-373 A5: the line to show for each `not_current_reason` the API can
 * send, in the order a reader meets them. A table rather than a ternary at the
 * call site, so adding a reason to the wire fails the build here (the key must
 * exist in `TranslationKey`) instead of silently rendering nothing. */
const STALE_REASONS: { reason: NotCurrentReason; reasonKey: TranslationKey }[] = [
  { reason: 'delisted', reasonKey: 'savedSeen.staleReasonDelisted' },
  { reason: 'ineligible', reasonKey: 'savedSeen.staleReasonIneligible' },
]

/** One group of no-longer-current rows: the reason the server stated for them,
 * or `null` for rows it stated none for — those still show, with no line. */
interface StaleGroup {
  reason: NotCurrentReason | null
  reasonKey: TranslationKey | null
  rows: Match[]
}

export default function SavedSeen() {
  const { t } = useI18n()
  const { data, loading, failed, reload, setData } = useApiResource(getSavedMatches, {
    cacheKey: SAVED_MATCHES_CACHE_KEY,
  })
  const matches = data ?? []

  // PIVOT-373 A5: taken out FIRST, so no stale row can appear under a
  // heading that implies it is still a live recommendation. The three
  // predicates stay exhaustive and non-overlapping.
  const stale = matches.filter((m) => !m.recommendation_current)
  const current = matches.filter((m) => m.recommendation_current)

  // PIVOT-141: see the docstring above — application_status wins over a
  // stale `not_interested` tap, so `dismissed` only holds rows with neither
  // an interested tap nor any funnel movement.
  const saved = current.filter(
    (m) => m.interest === 'interested' || m.application_status !== 'none',
  )
  const dismissed = current.filter(
    (m) => m.interest === 'not_interested' && m.application_status === 'none',
  )

  // PIVOT-373 A5: the stale rows, grouped by the reason the API stated, in a
  // fixed order, with a last group for a row that carries none. Read off
  // `not_current_reason` alone — this page never infers a reason from
  // `job.live`, which is the re-derivation the server field exists to remove.
  const staleGroups: StaleGroup[] = [
    ...STALE_REASONS.map(({ reason, reasonKey }) => ({
      reason,
      reasonKey,
      rows: stale.filter((m) => m.not_current_reason === reason),
    })),
    {
      reason: null,
      reasonKey: null,
      rows: stale.filter((m) => m.not_current_reason === null),
    },
  ].filter((group) => group.rows.length > 0)

  // PIVOT-133: which jobs' last write failed, per action — cleared the
  // moment a fresh attempt on that job starts, same pattern as Dashboard's
  // `interestErrors`. Three sets, not one, because a status write failing
  // must not paint an error under the interest buttons of the same row.
  const [interestErrors, setInterestErrors] = useState<ReadonlySet<string>>(new Set())
  const [statusErrors, setStatusErrors] = useState<ReadonlySet<string>>(new Set())
  const [outcomeErrors, setOutcomeErrors] = useState<ReadonlySet<string>>(new Set())
  // PIVOT-166: "this job no longer exists" — optimistic and one-way for this
  // session, same shape as Dashboard's identical state; nothing on `Match`
  // itself to roll back, so no `rollback()` call on failure, only the error flag.
  const [reportedGone, setReportedGone] = useState<ReadonlySet<string>>(new Set())
  const [reportGoneErrors, setReportGoneErrors] = useState<ReadonlySet<string>>(new Set())

  const clearError = (
    setErrors: React.Dispatch<React.SetStateAction<ReadonlySet<string>>>,
    jobId: string,
  ) => {
    setErrors((prev) => {
      if (!prev.has(jobId)) return prev
      const next = new Set(prev)
      next.delete(jobId)
      return next
    })
  }

  const rollback = (previous: Match) => {
    setData((prev) =>
      prev ? prev.map((m) => (m.job.id === previous.job.id ? previous : m)) : prev,
    )
  }

  const handleInterest = async (jobId: string, interest: Interest) => {
    const previous = matches.find((m) => m.job.id === jobId)
    clearError(setInterestErrors, jobId)
    setData((prev) =>
      prev ? prev.map((m) => (m.job.id === jobId ? { ...m, interest } : m)) : prev,
    )
    try {
      await setInterest(jobId, interest)
    } catch (reason) {
      if (isAbortError(reason)) return
      // Revert: a taste signal the server never recorded is still a signal
      // the card is lying about (frontend/CLAUDE.md §7) — PIVOT-133 removed
      // the exception this handler used to be.
      if (previous) rollback(previous)
      setInterestErrors((prev) => new Set(prev).add(jobId))
    }
  }

  const handleApplicationStatus = async (jobId: string, applicationStatus: ApplicationStatus) => {
    const previous = matches.find((m) => m.job.id === jobId)
    clearError(setStatusErrors, jobId)
    setData((prev) =>
      prev
        ? prev.map((m) =>
            m.job.id === jobId ? { ...m, application_status: applicationStatus } : m,
          )
        : prev,
    )
    try {
      await setApplicationStatus(jobId, applicationStatus)
    } catch (reason) {
      if (isAbortError(reason)) return
      // Roll back: the write failed, so the card must not keep showing a
      // status the server never recorded.
      if (previous) rollback(previous)
      setStatusErrors((prev) => new Set(prev).add(jobId))
    }
  }

  const handleOutcome = async (jobId: string, heardBack: boolean) => {
    const previous = matches.find((m) => m.job.id === jobId)
    clearError(setOutcomeErrors, jobId)
    setData((prev) =>
      prev
        ? prev.map((m) =>
            m.job.id === jobId
              ? {
                  ...m,
                  application_status: heardBack ? 'responded' : m.application_status,
                  outcome_due: false,
                }
              : m,
          )
        : prev,
    )
    try {
      await reportOutcome(jobId, heardBack)
    } catch (reason) {
      if (isAbortError(reason)) return
      // Roll back: an unanswered nudge that silently vanished on a failed
      // write would look answered when it never was.
      if (previous) rollback(previous)
      setOutcomeErrors((prev) => new Set(prev).add(jobId))
    }
  }

  const handleReportGone = async (jobId: string) => {
    clearError(setReportGoneErrors, jobId)
    setReportedGone((prev) => new Set(prev).add(jobId))
    try {
      await reportGone(jobId)
    } catch (reason) {
      if (isAbortError(reason)) return
      setReportedGone((prev) => {
        const next = new Set(prev)
        next.delete(jobId)
        return next
      })
      setReportGoneErrors((prev) => new Set(prev).add(jobId))
    }
  }

  // One row, drawn the same way in every section — `MatchListRow` renders the
  // `<li>` itself, so each section still supplies its own `<ul>`. Extracted
  // when PIVOT-373's reason groups made this a fourth call site for the same
  // eleven props.
  const listRow = (m: Match) => (
    <MatchListRow
      key={m.id}
      match={m}
      onInterest={handleInterest}
      onApplicationStatus={handleApplicationStatus}
      onOutcome={handleOutcome}
      onReportGone={handleReportGone}
      interestFailed={interestErrors.has(m.job.id)}
      statusFailed={statusErrors.has(m.job.id)}
      outcomeFailed={outcomeErrors.has(m.job.id)}
      reportedGone={reportedGone.has(m.job.id)}
      reportGoneFailed={reportGoneErrors.has(m.job.id)}
    />
  )

  return (
    <div className="saved-seen-page">
      <title>{`${t('savedSeen.title')} — ${t('common.appName')}`}</title>
      <div className="page-header">
        <h1>{t('savedSeen.title')}</h1>
      </div>
      {loading && <p role="status">{t('common.loading')}</p>}
      {failed && (
        <p className="form-error" role="alert">
          {t('common.error')}{' '}
          <button type="button" className="btn btn-ghost" onClick={() => void reload()}>
            {t('common.retry')}
          </button>
        </p>
      )}
      {!loading && !failed && matches.length === 0 && (
        // PIVOT-146: a dashed, quiet box plus a way out — the old version was a
        // title and one sentence on an otherwise dead-end page with nothing to
        // click. `dashboard.title` is the existing key so this link doesn't
        // invent new copy for "back to today's matches".
        <div className="saved-seen-empty">
          <h3>{t('savedSeen.emptyTitle')}</h3>
          <p>{t('savedSeen.emptyBody')}</p>
          <Link className="btn btn-outline" to="/dashboard">
            {t('dashboard.title')}
          </Link>
        </div>
      )}
      {stale.length > 0 && (
        <section>
          <h2>{t('savedSeen.sectionNoLongerCurrent')}</h2>
          <p className="saved-seen-stale-body">{t('savedSeen.sectionNoLongerCurrentBody')}</p>
          {staleGroups.map((group) => (
            <div className="saved-seen-stale-group" key={group.reason ?? 'unstated'}>
              {group.reasonKey !== null && (
                <p className="saved-seen-stale-reason">{t(group.reasonKey)}</p>
              )}
              <ul className="saved-seen-list">{group.rows.map(listRow)}</ul>
            </div>
          ))}
        </section>
      )}
      {saved.length > 0 && (
        <section>
          <h2>{t('savedSeen.sectionSaved')}</h2>
          <ul className="saved-seen-list">{saved.map(listRow)}</ul>
        </section>
      )}
      {dismissed.length > 0 && (
        <section>
          <h2>{t('savedSeen.sectionDismissed')}</h2>
          <ul className="saved-seen-list">{dismissed.map(listRow)}</ul>
        </section>
      )}
    </div>
  )
}
